import React, { useState, useEffect } from 'react';
import { securityAPI } from '../../services/api';
import './Sysc3Attestation.css';

const REQUIRED_CHECKS = [
  'Portal access controls and role-based permissions have been reviewed',
  'MFA is enforced for all administrative accounts',
  'Audit logs are complete and have been reviewed for the period',
  'Data retention and GDPR consent records are up to date',
  'Security headers and CSP are correctly configured',
  'Email notification system is operational (no outstanding failures)',
  'Account lockout controls are active',
  'Session timeout is configured and tested',
  'Complaints procedure link is accessible from the portal footer',
  'This attestation has been reviewed by Matt Murphy as Senior Responsible Manager',
];

interface PastAttestation {
  id: number;
  timestamp: string;
  seniorManager: string;
  notes: string;
  checks: string[];
}

const Sysc3Attestation: React.FC = () => {
  const [checked, setChecked]         = useState<boolean[]>(new Array(REQUIRED_CHECKS.length).fill(false));
  const [seniorManager, setSeniorManager] = useState('Matt Murphy');
  const [notes, setNotes]             = useState('');
  const [submitting, setSubmitting]   = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const [past, setPast]               = useState<PastAttestation[]>([]);
  const [pastLoading, setPastLoading] = useState(true);
  const [pastError, setPastError]     = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const allChecked = checked.every(Boolean);

  useEffect(() => {
    loadPast();
  }, []);

  const handleDownloadPdf = async (a: PastAttestation) => {
    setDownloadingId(a.id);
    try {
      const res = await securityAPI.downloadAttestationPdf(a.id);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `SYSC3-Attestation-${new Date(a.timestamp).toISOString().slice(0,10)}-${String(a.id).padStart(4, '0')}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      alert('PDF generation failed. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  const loadPast = () => {
    setPastLoading(true);
    setPastError(null);
    securityAPI.getAttestations()
      .then(res => setPast(res.data ?? []))
      .catch(() => setPastError('Failed to load past attestations.'))
      .finally(() => setPastLoading(false));
  };

  const toggleCheck = (index: number) => {
    setChecked(prev => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!allChecked || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);
    try {
      await securityAPI.submitAttestation({
        seniorManager: seniorManager.trim(),
        notes: notes.trim(),
        checks: REQUIRED_CHECKS,
        timestamp: new Date().toISOString(),
      });
      setSubmitSuccess(true);
      setChecked(new Array(REQUIRED_CHECKS.length).fill(false));
      setNotes('');
      loadPast();
    } catch {
      setSubmitError('Failed to submit attestation. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="sysc3">
      <div className="sysc3-card">
        <h3 className="sysc3-heading">SYSC 3 — Systems &amp; Controls Attestation</h3>
        <p className="sysc3-description">
          FCA SYSC 3 requires senior management to confirm that adequate systems and controls are in place.
          This attestation must be completed annually by Matt Murphy (Senior Manager).
        </p>

        <form onSubmit={handleSubmit} className="sysc3-form">
          <fieldset className="sysc3-fieldset">
            <legend className="sysc3-legend">Required Checks</legend>
            <p className="sysc3-check-hint">All items must be confirmed before signing.</p>
            <ul className="sysc3-checklist">
              {REQUIRED_CHECKS.map((label, i) => (
                <li key={i} className="sysc3-check-item">
                  <label className="sysc3-check-label">
                    <input
                      type="checkbox"
                      checked={checked[i]}
                      onChange={() => toggleCheck(i)}
                      className="sysc3-checkbox"
                    />
                    <span className="sysc3-check-text">{label}</span>
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>

          <div className="sysc3-field">
            <label className="sysc3-label" htmlFor="sysc3-manager">Senior Manager Name</label>
            <input
              id="sysc3-manager"
              type="text"
              className="sysc3-input"
              value={seniorManager}
              onChange={e => setSeniorManager(e.target.value)}
              required
            />
          </div>

          <div className="sysc3-field">
            <label className="sysc3-label" htmlFor="sysc3-notes">
              Attestation Notes <span className="sysc3-optional">(optional)</span>
            </label>
            <textarea
              id="sysc3-notes"
              className="sysc3-textarea"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={4}
              placeholder="Any additional observations or context..."
            />
          </div>

          {submitError && <p className="sysc3-error">{submitError}</p>}
          {submitSuccess && (
            <p className="sysc3-success">Attestation submitted successfully.</p>
          )}

          <button
            type="submit"
            className="sysc3-submit-btn"
            disabled={!allChecked || submitting || !seniorManager.trim()}
          >
            {submitting ? 'Submitting…' : 'Sign & Submit Attestation'}
          </button>
          {!allChecked && (
            <p className="sysc3-submit-hint">
              All {REQUIRED_CHECKS.length} checks must be ticked before submitting.
            </p>
          )}
        </form>
      </div>

      {/* Past attestations */}
      <div className="sysc3-past">
        <h4 className="sysc3-past-heading">Past Attestations</h4>
        {pastLoading && <p className="sysc3-loading">Loading…</p>}
        {pastError   && <p className="sysc3-error">{pastError}</p>}
        {!pastLoading && !pastError && past.length === 0 && (
          <p className="sysc3-empty">No attestations on record yet.</p>
        )}
        {!pastLoading && !pastError && past.length > 0 && (
          <table className="sysc3-past-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Senior Manager</th>
                <th>Notes</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {past.map(a => (
                <React.Fragment key={a.id}>
                  <tr className="sysc3-past-row">
                    <td>{new Date(a.timestamp).toLocaleDateString('en-GB', {
                      day: '2-digit', month: 'short', year: 'numeric',
                    })}</td>
                    <td>{a.seniorManager}</td>
                    <td className="sysc3-past-notes">{a.notes || <em>—</em>}</td>
                    <td>
                      <button
                        className="sysc3-view-btn"
                        onClick={() => handleDownloadPdf(a)}
                        disabled={downloadingId === a.id}
                        title="Download PDF"
                      >
                        {downloadingId === a.id ? 'Generating…' : '⬇ Download PDF'}
                      </button>
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Sysc3Attestation;
